import { useTranslations } from '@/hooks/useTranslations';
import { useStorefront } from '@/hooks/useStorefront';
import { groupIntFor } from '@/lib/localeFormat';
import { Users, Heart, MessageCircle, Bookmark, Play, Eye, ShieldCheck, Award, Sparkles } from 'lucide-react';

const TIERS = ['Lite', 'Basic', 'Pro'] as const;

const ROWS: { key: string; fallback: string; icon: React.ReactNode; values: [number, number, number] }[] = [
  { key: 'pkg.row_followers', fallback: 'Super Real Follower', icon: <Users className="h-4 w-4" />, values: [10, 50, 100] },
  { key: 'pkg.row_likes', fallback: 'Super Real Likes', icon: <Heart className="h-4 w-4" />, values: [25, 150, 300] },
  { key: 'pkg.row_comments', fallback: 'Echte Kommentare', icon: <MessageCircle className="h-4 w-4" />, values: [5, 30, 60] },
  { key: 'pkg.row_saves', fallback: 'Echte Speicherungen', icon: <Bookmark className="h-4 w-4" />, values: [5, 30, 60] },
  { key: 'pkg.row_views', fallback: 'Echte Video-Views', icon: <Play className="h-4 w-4" />, values: [1000, 1000, 1000] },
  { key: 'pkg.row_visits', fallback: 'Echte Profilbesuche', icon: <Eye className="h-4 w-4" />, values: [1000, 1000, 1000] },
];

/** True when a product's variations are the Lite/Basic/Pro growth packages. */
export function hasPackageTiers(labels: string[]): boolean {
  const set = new Set(labels.map((l) => l.trim().toLowerCase()));
  return TIERS.every((t) => set.has(t.toLowerCase()));
}

export default function PackageTiers({ selectedIndex }: { selectedIndex: number }) {
  const { t } = useTranslations();
  const { storefront } = useStorefront();

  return (
    <div className="mb-8">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
        <Sparkles className="h-5 w-5 text-primary" />
        {t('pkg.title', 'Das ist enthalten')}
      </h2>
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
              <th className="bg-card px-3 py-3 text-left font-medium">{t('pkg.package', 'Paket')}</th>
              {TIERS.map((tier, index) => {
                const isSelected = index === selectedIndex;
                return (
                  <th
                    key={tier}
                    className={`px-3 py-3 text-right font-semibold transition-colors ${
                      isSelected
                        ? 'border-x-2 border-primary bg-primary text-primary-foreground'
                        : 'bg-card text-muted-foreground'
                    }`}
                  >
                    {tier}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.key} className="border-b last:border-0">
                <td className="px-3 py-2.5 text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      {row.icon}
                    </span>
                    {t(row.key, row.fallback)}
                  </span>
                </td>
                {row.values.map((v, i) => {
                  const isSelected = i === selectedIndex;
                  return (
                    <td
                      key={i}
                      className={`px-3 py-2.5 text-right tabular-nums transition-colors ${
                        isSelected
                          ? 'border-x-2 border-primary bg-primary/15 font-semibold text-primary'
                          : 'bg-card text-foreground'
                      }`}
                    >
                      {groupIntFor(storefront, v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs font-medium text-foreground">
          <ShieldCheck className="h-4 w-4 shrink-0 text-success" />
          {t('pkg.badge_non_drop', 'Dauerhaft stabil')}
        </div>
        <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs font-medium text-foreground">
          <Award className="h-4 w-4 shrink-0 text-primary" />
          {t('pkg.badge_guarantee', 'Lebenslange Garantie')}
        </div>
      </div>
      <hr className="mt-6" />
    </div>
  );
}
